import { LoadingOutlined } from '@ant-design/icons';
import { ThemedLayout } from '@refinedev/antd';
import { Authenticated, ErrorComponent } from '@refinedev/core';
import { ConfigProvider } from 'antd';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';

import CountryEnforcer from '@/components/general/CountryEnforcer';
import ErrorBoundary from '@/components/general/ErrorBoundary';
import Header from '@/components/layout/Header/Header';
import CustomSider from '@/components/layout/Sider/Sider';
import ProtectedRoutes from '@/components/protected/ProtectedRoute';
import useConfigProvider from '@/hooks/useConfigProvider';
import { useFirebaseMessaging } from '@/hooks/useFirebaseMessaging';
import { RESOURCES } from '@/Resources';

import SparePartsCollectionList from './administration/SparePartsCollection/SparePartsCollectionList';
import SparePartsCollectionsSection from './administration/SparePartsCollection/SparePartsCollectionsSection';
import { LoginPage } from './auth/LoginPage';
import LoginSection from './auth/LoginSection';
import Home from './home/Home';
import CaseResolutionList from './logistic/CaseResolution/CaseResolutionList';
import CaseResolutionSection from './logistic/CaseResolution/CaseResolutionSection';
import CaseResolutionShow from './logistic/CaseResolution/CaseResolutionShow';
import { ReceptionCreate } from './logistic/Receptions/ReceptionCreate';
import { ReceptionEdit } from './logistic/Receptions/ReceptionEdit';
import ReceptionSection from './logistic/Receptions/ReceptionSection';
import { ReceptionShow } from './logistic/Receptions/ReceptionShow';
import { ReceptionsList } from './logistic/Receptions/ReceptionsList';
import { ShipmentCreate } from './logistic/Shipments/ShipmentCreate';
import { ShipmentEdit } from './logistic/Shipments/ShipmentEdit';
import ShipmentSection from './logistic/Shipments/ShipmentSection';
import { ShipmentShow } from './logistic/Shipments/ShipmentShow';
import { ShipmentsList } from './logistic/Shipments/ShipmentsList';
import ShipperCreate from './logistic/Shippers/ShipperCreate';
import ShipperEdit from './logistic/Shippers/ShipperEdit';
import ShipperSection from './logistic/Shippers/ShipperSection';
import ShipperShow from './logistic/Shippers/ShipperShow';
import ShippersList from './logistic/Shippers/ShippersList';
import { ShippingLabelCreate } from './logistic/ShippingLabels/ShippingLabelCreate';
import ShippingLabelEdit from './logistic/ShippingLabels/ShippingLabelEdit';
import { ShippingLabelShow } from './logistic/ShippingLabels/ShippingLabelShow';
import ShippingLabelsList from './logistic/ShippingLabels/ShippingLabelsList';
import ShippingLabelsSection from './logistic/ShippingLabels/ShippingLabelsSection';
import NotificationsSection from './notifications/NotificationsSection';
import ProductCatalogList from './productCatalog/ProductCatalogList';
import ProductCatalogSection from './productCatalog/ProductCatalogSection';
import ServiceOrderCreate from './ServiceOrder/ServiceOrderCreate';
import ServiceOrderShow from './ServiceOrder/ServiceOrderShow';
import ServiceOrdersList from './ServiceOrder/ServiceOrdersList';
import ServiceOrdersSection from './ServiceOrder/ServiceOrdersSection';
import TicketCreate from './tickets/TicketCreate';
import { TicketEdit } from './tickets/TicketEdit';
import TicketList from './tickets/TicketList';
import TicketSection from './tickets/TicketSection';
import { TicketShow } from './tickets/TicketShow';

/**
 * @metadata SafeLoginRedirect
 * Evita encadenar el query param `to` para no generar loops de redirección.
 */
const SafeLoginRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Evita que un `to` previo se re-encodee y crezca en cada redirección.
  searchParams.delete('to');

  const sanitizedSearch = searchParams.toString();
  const sanitizedPath = `${location.pathname}${sanitizedSearch ? `?${sanitizedSearch}` : ''}${
    location.hash ?? ''
  }`;
  const nextSearch = new URLSearchParams();

  nextSearch.set('to', sanitizedPath || '/');

  // Deja que el router agregue el basename para evitar duplicarlo.
  return <Navigate to={{ pathname: to, search: `?${nextSearch.toString()}` }} replace />;
};

// Mantiene estable la referencia del Header para que ThemedLayout no remonte el menu al cambiar idioma.
const LayoutHeader = () => <Header />;
// Mantiene estable la referencia del Sider junto con el Header dentro de ThemedLayout.
const LayoutSider = () => <CustomSider />;
const AppRoutes = () => {
  useFirebaseMessaging();
  const { antdLocale, theme } = useConfigProvider();

  return (
    <Routes>
      <Route
        element={
          <ConfigProvider locale={antdLocale} theme={theme}>
            <Authenticated key="authenticated-inner" fallback={<SafeLoginRedirect to="/login" />}>
              <ErrorBoundary>
                <ThemedLayout Header={LayoutHeader} Sider={LayoutSider}>
                  <ProtectedRoutes>
                    <CountryEnforcer>
                      <Outlet />
                    </CountryEnforcer>
                  </ProtectedRoutes>
                </ThemedLayout>
              </ErrorBoundary>
            </Authenticated>
          </ConfigProvider>
        }
      >
        <Route path="">
          <Route index element={<Home />} />
        </Route>
        <Route path="notifications" element={<NotificationsSection />} />
        <Route path={RESOURCES.TICKETS} element={<TicketSection />}>
          <Route index element={<TicketList />} />
          <Route path="show/:id" element={<TicketShow />} />
          <Route path="edit/:id" element={<TicketEdit />} />
          <Route path="create" element={<TicketCreate />} />
        </Route>
        <Route path={RESOURCES.SHIPMENTS} element={<ShipmentSection />}>
          <Route index element={<ShipmentsList />} />
          <Route path="show/:id" element={<ShipmentShow />} />
          <Route path="edit/:id" element={<ShipmentEdit />} />
          <Route path="create" element={<ShipmentCreate />} />
        </Route>
        <Route path={RESOURCES.SHIPPING_LABELS} element={<ShippingLabelsSection />}>
          <Route index element={<ShippingLabelsList />} />
          <Route path="show/:id" element={<ShippingLabelShow />} />
          <Route path="edit/:id" element={<ShippingLabelEdit />} />
          <Route path="create" element={<ShippingLabelCreate />} />
        </Route>
        <Route path={RESOURCES.SHIPPERS} element={<ShipperSection />}>
          <Route index element={<ShippersList />} />
          <Route path="show/:id" element={<ShipperShow />} />
          <Route path="edit/:id" element={<ShipperEdit />} />
          <Route path="create" element={<ShipperCreate />} />
        </Route>
        <Route path={RESOURCES.RECEPTIONS} element={<ReceptionSection />}>
          <Route index element={<ReceptionsList />} />
          <Route path="show/:id" element={<ReceptionShow />} />
          <Route path="edit/:id" element={<ReceptionEdit />} />
          <Route path="create" element={<ReceptionCreate />} />
        </Route>
        <Route path={RESOURCES.SERVICE_ORDERS} element={<ServiceOrdersSection />}>
          <Route index element={<ServiceOrdersList />} />
          <Route path="create" element={<ServiceOrderCreate />} />
          <Route path="show/:id" element={<ServiceOrderShow />} />
        </Route>
        <Route path={RESOURCES.CASE_RESOLUTION} element={<CaseResolutionSection />}>
          <Route index element={<CaseResolutionList />} />
          <Route path=":id" element={<CaseResolutionShow />} />
        </Route>
        <Route path={RESOURCES.SPARE_PARTS_COLLECTIONS} element={<SparePartsCollectionsSection />}>
          <Route index element={<SparePartsCollectionList />} />
          <Route path=":id" element={<ShipmentShow />} />
        </Route>
        <Route path={RESOURCES.PRODUCT_CATALOG} element={<ProductCatalogSection />}>
          <Route index element={<ProductCatalogList />} />
        </Route>
        <Route path="*" element={<ErrorComponent />} />
      </Route>
      <Route
        element={
          <Authenticated
            key="authenticated-pages"
            fallback={<Outlet />}
            loading={<LoadingOutlined />}
          >
            <Navigate to={'/'} />
          </Authenticated>
        }
      >
        <Route path="login" element={<LoginSection />}>
          <Route index element={<LoginPage />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRoutes;
